import React, { useState } from 'react';
import { Box, Wrench, Monitor, Package, FileText, Download, Droplet, Archive, Circle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export default function FinanceAssets() {
  const [activeTab, setActiveTab] = useState('bahan_baku');

  const tabs = [
    { id: 'bahan_baku', label: 'Bahan Baku', icon: Box },
    { id: 'mesin_alat', label: 'Mesin & Alat', icon: Wrench },
    { id: 'peralatan_kantor', label: 'Peralatan Kantor', icon: Monitor },
  ];

  // Fetch Data for Bahan Baku
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });
  const { data: bahanCairList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });

  const calcAsset = (list) => {
    return list.reduce((acc, curr) => {
      const itemData = curr.data || curr;
      // Jangan hitung barang yang sudah dihapus / di keranjang sampah
      if (itemData.deleted_at || itemData.status === 'deleted') {
        return acc;
      }
      // Jangan hitung barang milik client/titipan
      if (itemData.dari_client === true || String(itemData.dari_client) === 'true') {
        return acc;
      }
      const stok = Number(itemData.stok || 0);
      const harga = Number(itemData.harga_rupiah || itemData.harga_sebelum_ppn || itemData.harga_jual || 0);
      return acc + (stok * harga);
    }, 0);
  };

  const assetBotol = calcAsset(botolList);
  const assetTutup = calcAsset(tutupList);
  const assetSpray = calcAsset(sprayList);
  const assetFullset = calcAsset(fullsetList);

  // Dinamis kategori untuk Bahan Cair
  const bahanCairCategories = [
    'Bibit', 'Alkohol', 'Aqua Des', 'DPG', 'PEG', 'Sustain', 'Quidex', 'Castor Oil', 'Lainnya'
  ];
  
  const groupedBahanCair = bahanCairCategories.map(cat => {
    const list = bahanCairList.filter(b => {
      const bKat = (b.data?.kategori || b.kategori || '').toLowerCase();
      if (cat === 'Lainnya') {
        return !bahanCairCategories.some(c => c !== 'Lainnya' && bKat === c.toLowerCase());
      }
      return bKat === cat.toLowerCase();
    });
    return {
      kategori: cat,
      count: list.length,
      asset: calcAsset(list)
    };
  }).filter(g => g.count > 0 || g.asset > 0); // Hanya tampilkan yang ada datanya

  const totalAssetBahanCair = groupedBahanCair.reduce((sum, g) => sum + g.asset, 0);
  const totalAssetPackaging = assetBotol + assetTutup + assetSpray + assetFullset;

  const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  const totalAssetBahanBaku = totalAssetPackaging + totalAssetBahanCair;

  return (
    <div className="flex flex-col h-full bg-[#FAFAFA]">
      {/* Header Section */}
      <div className="bg-white border-b border-[#E6D5B8]/40 sticky top-0 z-20">
        <div className="px-6 py-6 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-black text-[#302621] tracking-tight flex items-center gap-3">
                <Package className="w-8 h-8 text-[#8C6208]" />
                Aset Perusahaan
              </h1>
              <p className="text-[#7A695B] mt-2 font-medium">
                Kelola inventaris bahan baku, mesin, alat, dan peralatan kantor.
              </p>
            </div>
            <div className="flex gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E6D5B8] text-[#302621] rounded-xl text-sm font-semibold hover:bg-[#FAFAFA] transition-all shadow-sm">
                <FileText className="w-4 h-4 text-[#8C6208]" />
                Laporan
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#8C6208] text-white rounded-xl text-sm font-bold hover:bg-[#704E06] hover:-translate-y-0.5 transition-all shadow-sm">
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Tab Navigation (Pill Style) */}
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all border whitespace-nowrap ${
                    isActive
                      ? 'bg-[#FDFBF7] border-[#8C6208] text-[#8C6208] shadow-sm'
                      : 'bg-white border-[#E6D5B8] text-[#7A695B] hover:border-[#8C6208]/50 hover:text-[#302621]'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-[#8C6208]' : 'text-slate-400'}`} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-8 max-w-7xl mx-auto">
          {activeTab === 'bahan_baku' ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between px-6 pt-6 pb-2">
                <div>
                  <h2 className="text-xl font-bold text-[#302621]">Total Nilai Aset Bahan Baku</h2>
                  <p className="text-sm text-[#7A695B]">Keseluruhan nilai dari packaging dan bahan cair di inventori</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-[#7A695B] uppercase tracking-wider mb-1">Estimasi Total</p>
                  <p className="text-3xl font-black text-[#8C6208]">Rp {fmt(totalAssetBahanBaku)}</p>
                </div>
              </div>

              <div className="px-6 pb-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Packaging Section */}
                <Card className="border-[#E6D5B8]/40 shadow-sm bg-[#FAFAFA]/50">
                  <CardHeader className="pb-4 flex flex-row items-center justify-between border-b border-[#E6D5B8]/20 mb-4">
                    <CardTitle className="text-lg flex items-center gap-2 text-[#302621]">
                      <Package className="w-5 h-5 text-[#A39171]" />
                      Packaging
                    </CardTitle>
                    <div className="text-right">
                      <p className="text-[10px] font-bold text-[#7A695B] uppercase tracking-widest">Total Packaging</p>
                      <p className="text-base font-black text-[#8C6208]">Rp {fmt(totalAssetPackaging)}</p>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-white p-4 rounded-xl border border-[#E6D5B8]/60 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FDFBF7] rounded-full flex items-center justify-center border border-[#E6D5B8]/40">
                          <Archive className="w-5 h-5 text-[#8C6208]" />
                        </div>
                        <div>
                          <p className="font-bold text-[#302621] text-sm">Asset Botol</p>
                          <p className="text-xs text-[#7A695B]">{botolList.length} jenis item</p>
                        </div>
                      </div>
                      <p className="font-bold text-[#302621]">Rp {fmt(assetBotol)}</p>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-[#E6D5B8]/60 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FDFBF7] rounded-full flex items-center justify-center border border-[#E6D5B8]/40">
                          <Circle className="w-5 h-5 text-[#8C6208]" />
                        </div>
                        <div>
                          <p className="font-bold text-[#302621] text-sm">Asset Tutup</p>
                          <p className="text-xs text-[#7A695B]">{tutupList.length} jenis item</p>
                        </div>
                      </div>
                      <p className="font-bold text-[#302621]">Rp {fmt(assetTutup)}</p>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-[#E6D5B8]/60 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FDFBF7] rounded-full flex items-center justify-center border border-[#E6D5B8]/40">
                          <Box className="w-5 h-5 text-[#8C6208]" />
                        </div>
                        <div>
                          <p className="font-bold text-[#302621] text-sm">Asset Spray</p>
                          <p className="text-xs text-[#7A695B]">{sprayList.length} jenis item</p>
                        </div>
                      </div>
                      <p className="font-bold text-[#302621]">Rp {fmt(assetSpray)}</p>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-[#E6D5B8]/60 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FDFBF7] rounded-full flex items-center justify-center border border-[#E6D5B8]/40">
                          <Package className="w-5 h-5 text-[#8C6208]" />
                        </div>
                        <div>
                          <p className="font-bold text-[#302621] text-sm">Asset Fullset</p>
                          <p className="text-xs text-[#7A695B]">{fullsetList.length} jenis item</p>
                        </div>
                      </div>
                      <p className="font-bold text-[#302621]">Rp {fmt(assetFullset)}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Bahan Cair Section */}
                <Card className="border-[#E6D5B8]/40 shadow-sm bg-[#FAFAFA]/50">
                  <CardHeader className="pb-4 flex flex-row items-center justify-between border-b border-[#E6D5B8]/20 mb-4">
                    <CardTitle className="text-lg flex items-center gap-2 text-[#302621]">
                      <Droplet className="w-5 h-5 text-[#A39171]" />
                      Bahan Cair
                    </CardTitle>
                    <div className="text-right">
                      <p className="text-[10px] font-bold text-[#7A695B] uppercase tracking-widest">Total Bahan Cair</p>
                      <p className="text-base font-black text-[#8C6208]">Rp {fmt(totalAssetBahanCair)}</p>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {groupedBahanCair.map((group, idx) => (
                      <div key={idx} className="bg-white p-4 rounded-xl border border-[#E6D5B8]/60 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                            group.kategori === 'Bibit' ? 'bg-amber-50 border-amber-200/50 text-amber-600' :
                            group.kategori === 'Alkohol' ? 'bg-blue-50 border-blue-200/50 text-blue-600' :
                            'bg-slate-50 border-slate-200/50 text-slate-500'
                          }`}>
                            <Droplet className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-bold text-[#302621] text-sm">Asset {group.kategori}</p>
                            <p className="text-xs text-[#7A695B]">{group.count} jenis item</p>
                          </div>
                        </div>
                        <p className="font-bold text-[#302621]">Rp {fmt(group.asset)}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <Card className="border-[#E6D5B8]/60 shadow-sm bg-white rounded-2xl overflow-hidden mt-6 mx-6 mb-8">
              <CardHeader className="bg-[#FDFBF7] border-b border-[#E6D5B8]/40 py-5">
                <CardTitle className="text-lg text-[#302621]">
                  {tabs.find(t => t.id === activeTab)?.label}
                </CardTitle>
                <CardDescription className="text-[#7A695B]">
                  Data akan ditampilkan di sini.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-12 flex flex-col items-center justify-center text-center">
                <div className="w-24 h-24 bg-[#FDFBF7] border border-[#E6D5B8] rounded-full flex items-center justify-center mb-6">
                  <Package className="w-10 h-10 text-[#A39171]" />
                </div>
                <h3 className="text-xl font-bold text-[#302621] mb-2">Belum ada data</h3>
                <p className="text-[#7A695B] max-w-sm mx-auto">
                  Tabel untuk <strong>{tabs.find(t => t.id === activeTab)?.label}</strong> masih kosong. Silakan tambahkan data atau integrasikan dengan database.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
